'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { Button } from '@/app/components/ui/button';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { useToast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { cn } from '@/app/utils';
import type { LiveCallConsentResult } from '../_hooks/live-call-consent';

/**
 * - avatar: live rules and voiceprint consent are required, face consent is optional.
 * - editing: all three consents are required and confirmed together.
 */
export type LiveCallConsentMode = 'avatar' | 'editing';

type ChinaConsentItem = 'live' | 'voice' | 'face';

// China-only copy; the global site keeps the translated single-item dialog.
const CHINA_CONSENT_COPY = {
  avatarDescription: '当前功能可能涉及敏感个人信息处理，使用前需先阅读并同意以下内容；勾选同意后才可继续使用。',
  editingDescription: '当前功能可能涉及敏感个人信息处理，使用前需先阅读并同意以下内容；全部勾选同意后才可继续使用。',
  agreePrefix: '我已阅读并同意',
  selectAll: '一键同意以上',
  requireFirstTwo: '请阅读并同意《视频通话的个人信息处理规则》和《敏感个人信息收集处理授权同意书（声纹特征）》',
  agree: '同意',
  agreeAll: '一键同意',
};

const CHINA_CONSENT_ITEMS: { id: ChinaConsentItem; href: string; linkText: string }[] = [
  { id: 'live', href: '/consent-live', linkText: '《视频通话的个人信息处理规则》' },
  { id: 'voice', href: '/consent-voice', linkText: '《敏感个人信息收集处理授权同意书（声纹特征）》' },
  { id: 'face', href: '/consent-face', linkText: '《敏感个人信息收集处理授权同意书（面部识别特征）》' },
];

const SELECT_ALL_CHECKBOX_ID = 'live-call-consent-select-all';
const EMPTY_CHINA_CONSENT: Record<ChinaConsentItem, boolean> = { live: false, voice: false, face: false };
const FULL_CHINA_CONSENT: Record<ChinaConsentItem, boolean> = { live: true, voice: true, face: true };

interface LiveCallConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (result: LiveCallConsentResult) => void;
  /** Only affects the China site. */
  mode?: LiveCallConsentMode;
}

export function LiveCallConsentModal({ open, onOpenChange, onConfirm, mode = 'avatar' }: LiveCallConsentModalProps) {
  const t = useTranslations('vidu-stream.compliance');
  const { toast } = useToast();
  const [hasAgreed, setHasAgreed] = useState(false);
  const [chinaConsent, setChinaConsent] = useState(EMPTY_CHINA_CONSENT);
  const isChinaSite = SITE === 'china';
  const isAllChinaConsentChecked = chinaConsent.live && chinaConsent.voice && chinaConsent.face;

  useEffect(() => {
    if (open) {
      setHasAgreed(false);
      setChinaConsent(EMPTY_CHINA_CONSENT);
    }
  }, [open]);

  const confirmAndClose = (result: LiveCallConsentResult) => {
    onConfirm(result);
    onOpenChange(false);
  };

  const handleChinaConfirm = () => {
    if (mode === 'editing') {
      setChinaConsent(FULL_CHINA_CONSENT);
      confirmAndClose({ isFaceConsentAgreed: true });
      return;
    }
    if (!chinaConsent.live || !chinaConsent.voice) {
      toast({ type: 'warning', message: CHINA_CONSENT_COPY.requireFirstTwo });
      return;
    }
    confirmAndClose({ isFaceConsentAgreed: chinaConsent.face });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
      }}
    >
      <DialogContent
        className={cn(
          'max-w-none sm:w-[calc(100%-24px)] sm:gap-3 sm:p-4',
          // The face consent title needs ~600px to stay on one line.
          isChinaSite ? 'w-[min(600px,calc(100%-32px))]' : 'w-[min(560px,calc(100%-32px))]',
        )}
        closable
        onClose={() => onOpenChange(false)}
        closeClassName="sm:top-4 sm:right-4"
        disableOverlayClose
      >
        <DialogHeader className="gap-2 sm:gap-1 sm:pr-10">
          <DialogTitle className="break-words text-left text-xl sm:text-base">{t('title')}</DialogTitle>
          <DialogDescription
            className={
              isChinaSite
                ? 'break-words text-left text-sm text-system-error1 leading-6 sm:text-xs sm:leading-5'
                : 'break-words text-left text-sm leading-6 sm:text-xs sm:leading-5'
            }
          >
            {isChinaSite
              ? mode === 'editing'
                ? CHINA_CONSENT_COPY.editingDescription
                : CHINA_CONSENT_COPY.avatarDescription
              : t('description')}
          </DialogDescription>
        </DialogHeader>

        {isChinaSite ? (
          <div className="space-y-4 sm:space-y-3">
            <div className="space-y-2">
              {CHINA_CONSENT_ITEMS.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 rounded-12 border border-system-white08 bg-system-white04 p-4 sm:gap-2.5 sm:p-3"
                >
                  <Checkbox
                    aria-label={item.linkText}
                    className="mt-0.5 shrink-0 text-base text-system-white sm:mt-0"
                    checked={chinaConsent[item.id]}
                    onCheckedChange={(checked) =>
                      setChinaConsent((current) => ({ ...current, [item.id]: Boolean(checked) }))
                    }
                  />
                  <span className="min-w-0 flex-1 text-sm text-system-white leading-6 sm:text-xs sm:leading-5">
                    {CHINA_CONSENT_COPY.agreePrefix}
                    <ClientLink
                      className="mx-1 text-system-white underline underline-offset-4"
                      href={item.href}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {item.linkText}
                    </ClientLink>
                  </span>
                </div>
              ))}
            </div>

            <p className="text-sm text-system-white64 leading-6 sm:text-xs sm:leading-5">
              {t('privacyPrefix')}
              <ClientLink
                className="text-system-white underline underline-offset-4"
                href="/privacy"
                rel="noreferrer"
                target="_blank"
              >
                {t('privacyLinkText')}
              </ClientLink>
            </p>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 sm:flex-col-reverse sm:items-stretch sm:gap-2 sm:pt-1">
              {mode === 'avatar' ? (
                <div className="flex items-center gap-2 text-sm text-system-white leading-6 sm:text-xs">
                  <Checkbox
                    id={SELECT_ALL_CHECKBOX_ID}
                    className="shrink-0 text-base text-system-white"
                    checked={isAllChinaConsentChecked}
                    onCheckedChange={(checked) => setChinaConsent(checked ? FULL_CHINA_CONSENT : EMPTY_CHINA_CONSENT)}
                  />
                  <label htmlFor={SELECT_ALL_CHECKBOX_ID} className="cursor-pointer">
                    {CHINA_CONSENT_COPY.selectAll}
                  </label>
                </div>
              ) : (
                <span />
              )}
              <div className="flex flex-wrap justify-end gap-3 sm:flex-col-reverse sm:items-stretch sm:gap-2">
                <Button variant="outline" size="dialog" className="sm:w-full" onClick={() => onOpenChange(false)}>
                  {t('cancel')}
                </Button>
                <Button variant="primary" size="dialog" className="sm:w-full" onClick={handleChinaConfirm}>
                  {mode === 'editing' ? CHINA_CONSENT_COPY.agreeAll : CHINA_CONSENT_COPY.agree}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-3">
            <div className="rounded-16 bg-system-white04 p-4 text-sm text-system-white64 leading-6 sm:p-3 sm:text-xs sm:leading-5">
              {t('tip')}
            </div>

            <div className="flex items-start gap-3 rounded-12 border border-system-white08 bg-system-white04 p-4 sm:gap-2.5 sm:p-3">
              <Checkbox
                className="mt-0.5 shrink-0 text-base text-system-white sm:mt-0"
                checked={hasAgreed}
                onCheckedChange={(checked) => setHasAgreed(Boolean(checked))}
              />
              <span className="min-w-0 flex-1 text-sm text-system-white leading-6 sm:text-xs sm:leading-5">
                {t('agreePrefix')}
                <ClientLink
                  className="mx-1 text-system-white underline underline-offset-4"
                  href="/consent-live"
                  rel="noreferrer"
                  target="_blank"
                >
                  {t('rulesLinkText')}
                </ClientLink>
              </span>
            </div>

            <p className="text-sm text-system-white64 leading-6 sm:text-xs sm:leading-5">
              {t('privacyPrefix')}
              <ClientLink
                className="text-system-white underline underline-offset-4"
                href="/privacy"
                rel="noreferrer"
                target="_blank"
              >
                {t('privacyLinkText')}
              </ClientLink>
            </p>

            <div className="flex flex-wrap justify-end gap-3 pt-2 sm:flex-col-reverse sm:items-stretch sm:gap-2 sm:pt-1">
              <Button variant="outline" size="dialog" className="sm:w-full" onClick={() => onOpenChange(false)}>
                {t('cancel')}
              </Button>
              <Button
                variant="primary"
                size="dialog"
                className="disabled:bg-none disabled:bg-system-neutral02 disabled:text-system-white24 disabled:opacity-100 sm:w-full"
                disabled={!hasAgreed}
                onClick={() => confirmAndClose({ isFaceConsentAgreed: false })}
              >
                {t('confirm')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
